/*
 * the month-close switch chip. Pure: the chip's text and tone, the confirm wording, the POST body
 * and the error handling are decided here and unit-tested.
 */
import { api_error, is_permission_error } from '../format';
import { with_ist } from '../shared/ist';
import { reason_error, trim_reason } from '../needs_attention/params';

export const CODE_CHANGED = 'CLOSE_SWITCH_CHANGED';

const on_off = (on) => (on ? 'ON' : 'OFF');

// What the hook holds after a read failed for a reason other than a missing grant.
export const SWITCH_UNKNOWN = { unknown: true };

// A read failure: no grant hides the chip (null); anything else shows it as unknown.
export const read_failed_data = (e) => (is_permission_error(e) ? null : SWITCH_UNKNOWN);

// null until the first read lands (or without a grant): the chip simply is not shown.
export const switch_view = (data) => {
  if (data && data.unknown === true) {
    return {
      enabled: null,
      can_toggle: false,
      label: 'Month close: unknown',
      tone: 'unknown',
      title: 'Could not read the month-close switch. Reload the page to check it.'
    };
  }
  if (!data || typeof data.close_enabled !== 'boolean') return null;
  const enabled = data.close_enabled;
  const last = data.last_change;
  const state = enabled
    ? 'Month close is ON: the payments team can close and reopen months.'
    : 'Month close is OFF: close and reopen are refused.';
  const changed =
    last && last.by_name
      ? ` Turned ${on_off(last.to)} by ${last.by_name} on ${with_ist(last.at_ist)}. Reason: ${last.reason}`
      : '';
  return {
    enabled,
    can_toggle: data.can_toggle === true,
    label: `Month close: ${on_off(enabled)}`,
    tone: enabled ? 'on' : 'off',
    title: `${state}${changed}`
  };
};

// What the confirm step says will happen when the switch moves to `target`.
export const confirm_text = (target) =>
  target
    ? 'Turning month close ON lets the payments team close and reopen months. Month runs that paused while it was off stay paused until someone presses Resume.'
    : 'Turning month close OFF refuses every close and reopen. Month runs that are running pause and need a person to press Resume once it is back ON.';

export const confirm_label = (target) => `Turn month close ${on_off(target)}`;

export const switch_reason_error = reason_error;

// expected_enabled is what this page showed, so a flip made meanwhile by someone else is refused (409).
export const switch_payload = (target, shown, reason) => ({
  enabled: target,
  expected_enabled: shown,
  reason: trim_reason(reason)
});

/* A failed POST, as the dialog shows it. */
export const switch_error = (e) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (code === CODE_CHANGED) {
    return {
      text: message || 'Month close was switched by someone else. Check it and try again.',
      reload: true
    };
  }
  if (status === 403) {
    return {
      text: message || 'Only a Teacher Payment super user can switch month close on or off.',
      reload: false
    };
  }
  // No answer or a 5xx: the flip may have committed, so say so and re-read the switch.
  if (!status || status >= 500) {
    return {
      text: 'No clear answer from the server: month close may have changed. It is being re-read; check it before trying again.',
      reload: true
    };
  }
  return {
    text: message || 'Could not change month close. Nothing was changed. Try again.',
    reload: false
  };
};
